import { tenantBrandingString, type TenantBrandingRecord } from '@/services/api/tenant-branding'

const FALLBACK_APP_NAME = 'Ticket Buddy - Ticket Admin'
const FALLBACK_SHORT_NAME = 'TB Admin'
const FALLBACK_THEME_COLOR = '#6366f1'
const FALLBACK_ICON_URL = '/images/ticket-buddy-app-icon.png'
const FALLBACK_ICON_TYPE = 'image/png'

let manifestObjectUrl: string | null = null

type PwaIconResource = {
  src: string
  sizes: string
  type: string
}

function normalizeHexColor(value: string): string {
  const trimmed = value.trim()
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed
  if (/^[0-9a-f]{6}$/i.test(trimmed)) return `#${trimmed}`
  return FALLBACK_THEME_COLOR
}

function firstBrandingString(record: TenantBrandingRecord, keys: string[]): string {
  for (const key of keys) {
    const value = tenantBrandingString(record, key).trim()
    if (value) return value
  }
  return ''
}

function upsertLink(rel: string, href: string, attrs: Record<string, string> = {}): void {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
  Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value))
}

function upsertThemeColor(color: string): void {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = color
}

function upsertDescription(description: string): void {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'description'
    document.head.appendChild(meta)
  }
  meta.content = description
}

function appUrl(path: string): string {
  return new URL(path, window.location.origin).href
}

function manifestResourceUrl(src: string): string {
  if (/^(?:https?:|data:)/i.test(src)) return src
  return appUrl(src.startsWith('/') ? src : `/${src}`)
}

function iconTypeFromUrl(url: string): string {
  if (url.startsWith('data:image/svg+xml')) return 'image/svg+xml'
  if (url.startsWith('data:image/webp')) return 'image/webp'
  if (url.startsWith('data:image/jpeg') || url.startsWith('data:image/jpg')) return 'image/jpeg'
  if (url.startsWith('data:image/png')) return 'image/png'
  const clean = url.split('?')[0]?.toLowerCase() || ''
  if (clean.endsWith('.svg')) return 'image/svg+xml'
  if (clean.endsWith('.webp')) return 'image/webp'
  if (clean.endsWith('.jpg') || clean.endsWith('.jpeg')) return 'image/jpeg'
  return 'image/png'
}

function iconSizesFromUrl(url: string): string {
  return iconTypeFromUrl(url) === 'image/svg+xml' ? 'any' : '144x144'
}

function revokePreviousManifestUrl(): void {
  if (!manifestObjectUrl) return
  URL.revokeObjectURL(manifestObjectUrl)
  manifestObjectUrl = null
}

function tenantIconUrl(record: TenantBrandingRecord): string {
  return firstBrandingString(record, ['appLogoUrl', 'faviconUrl', 'websiteLogoUrl', 'logoUrl']) || FALLBACK_ICON_URL
}

function tenantFaviconUrl(record: TenantBrandingRecord): string {
  return firstBrandingString(record, ['faviconUrl', 'appLogoUrl', 'websiteLogoUrl', 'logoUrl']) || FALLBACK_ICON_URL
}

function buildIconResource(src: string, sizes = iconSizesFromUrl(src)): PwaIconResource {
  return {
    src,
    sizes,
    type: iconTypeFromUrl(src),
  }
}

export function buildTenantPwaManifest(
  record: TenantBrandingRecord,
  iconResource = buildIconResource(tenantIconUrl(record)),
): Record<string, unknown> {
  const appName = firstBrandingString(record, ['appDisplayName', 'shopName', 'shortName']) || FALLBACK_APP_NAME
  const shortName = firstBrandingString(record, ['shortName', 'shopName', 'appDisplayName']) || FALLBACK_SHORT_NAME
  const themeColor = normalizeHexColor(firstBrandingString(record, ['primaryColor', 'accentColor']))
  const resolvedIcon = iconResource.src
    ? iconResource
    : buildIconResource(FALLBACK_ICON_URL, '1024x1024')

  return {
    id: appUrl('/'),
    name: appName,
    short_name: shortName.slice(0, 24),
    description: `${appName} admin`,
    start_url: appUrl('/'),
    display: 'standalone',
    background_color: '#ffffff',
    theme_color: themeColor,
    orientation: 'any',
    scope: appUrl('/'),
    icons: [
      {
        src: manifestResourceUrl(resolvedIcon.src),
        sizes: resolvedIcon.sizes,
        type: resolvedIcon.type,
        purpose: 'any',
      },
    ],
  }
}

export function applyTenantPwaBranding(record: TenantBrandingRecord): void {
  const faviconUrl = tenantFaviconUrl(record)
  const appIconUrl = tenantIconUrl(record)
  const manifest = buildTenantPwaManifest(record, buildIconResource(FALLBACK_ICON_URL, '1024x1024'))
  const themeColor = String(manifest.theme_color || FALLBACK_THEME_COLOR)
  const description = String(manifest.description || `${FALLBACK_APP_NAME} admin`)

  upsertLink('icon', faviconUrl, { type: iconTypeFromUrl(faviconUrl) || FALLBACK_ICON_TYPE })
  upsertLink('apple-touch-icon', appIconUrl)
  upsertThemeColor(themeColor)
  upsertDescription(description)

  const img = new Image()
  img.onload = () => {
    const iconIsLargeEnough = img.naturalWidth >= 144 && img.naturalHeight >= 144
    const iconIsSquare = img.naturalWidth === img.naturalHeight
    writeManifest(
      record,
      iconIsLargeEnough && iconIsSquare
        ? buildIconResource(appIconUrl, `${img.naturalWidth}x${img.naturalHeight}`)
        : buildIconResource(FALLBACK_ICON_URL, '1024x1024'),
    )
  }
  img.onerror = () => writeManifest(
    record,
    buildIconResource(FALLBACK_ICON_URL, '1024x1024'),
  )
  img.src = appIconUrl

  writeManifest(record, buildIconResource(FALLBACK_ICON_URL, '1024x1024'))
}

function writeManifest(record: TenantBrandingRecord, iconResource: PwaIconResource): void {
  const existingLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  revokePreviousManifestUrl()
  const manifest = buildTenantPwaManifest(record, iconResource)
  manifestObjectUrl = URL.createObjectURL(
    new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }),
  )
  if (existingLink) {
    existingLink.href = manifestObjectUrl
  } else {
    upsertLink('manifest', manifestObjectUrl)
  }
}
